import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'

const NEST_API_URL = 'https://api.nest.credit/v1'

/**
 * Nest wrapper-vault address (lowercase) → assetGroup.
 * Sourced from /v1/vaults/apy. Picks the Plume assetGroup variant in every
 * case except nALPHA, where the chain-1 spelling ("Alpha", not "ALPHA") is
 * the canonical one.
 */
const NEST_VAULT_GROUPS: Record<string, string> = {
  '0x119dd7daff816f29d7ee47596ae5e4bdc4299165':
    'Nest BlackOpal LiquidStone II Vault::nOPAL',
  '0x2a3e301dbd45c143dfbb7b1ce1c55bf0bbf161cb':
    'Nest Apollo ACRDX Vault::nACRDX',
  '0xe72fe64840f4ef80e3ec73a1c749491b5c938cb9': 'Nest Treasuries Vault::nTBILL',
  '0x29bf22381a5811dec89dc7b46a5ce57ad02c0240':
    'Nest WisdomTree Vault::nWISDOM',
  '0x593ccca4c4bf58b7526a4c164ceef4003c6388db': 'Nest Alpha Vault::nALPHA',
  '0x11113ff3a60c2450f4b22515cb760417259ee94b': 'Nest Basis Vault::nBASIS',
  '0xa5f78b2a0ab85429d2dfbf8b60abc70f4cec066c': 'Nest Credit Vault::nCREDIT',
  '0x7488b23f4c26b44eef2e0766896be47443e86d79':
    'Nest USDC PayFi Lending Vault::nAXI',
  '0xd99076fcfd61b3695c5a00740364a84ac8c46cba': 'Nest Perena Vault::nPERENA',
  '0xdf45b8322ea4ce898331602e2d1f3d1a67ae0ee8':
    'Nest Liquid Credit Vault::nLCRD',
  '0x770c2d6b16c8f8ab5535ae719a5475411c120f6e':
    'Nest Hamilton Lane SCOPE Vault::nSCOPE',
}

/**
 * Slugs for directly-issued RWA tokens. These are NOT in /v1/vaults/apy and
 * still have to be fetched per-slug.
 */
const NEST_RWA_SLUGS: { slug: string; group: string }[] = [
  {
    slug: 'janus-henderson-fund',
    group: 'Janus Henderson Anemoy Treasury Fund::JTRSY',
  },
  {
    slug: 'superstate-ustb',
    group: 'Superstate Short Duration US Government Securities Fund::USTB',
  },
  {
    slug: 'superstate-uscc',
    group: 'Superstate Crypto Carry Fund::USCC',
  },
  {
    slug: 'apollo-acrdx',
    group: 'Anemoy Tokenized Apollo Diversified Credit Fund::ACRDX',
  },
  {
    slug: 'blackstone-srln',
    group: 'Dinari SRLN::SRLN.D',
  },
]

interface NestApy {
  rolling7d: number | null
  rolling30d: number | null
  sec30d: number | null
}

interface NestVaultsApyResponse {
  data: { vaultAddress: string; apy: NestApy; navApy: NestApy }[]
}

interface NestSlugApyResponse {
  data: { slug: string; tokens: { apy: NestApy }[] }
}

const pickApy = (apy?: NestApy | null): number | undefined => {
  if (!apy) return undefined
  if (typeof apy.rolling30d === 'number' && apy.rolling30d > 0)
    return apy.rolling30d
  if (typeof apy.rolling7d === 'number' && apy.rolling7d > 0)
    return apy.rolling7d
  return undefined
}

// Nest returns decimal APY (e.g. 0.0334 = 3.34%); convert to APR %
const toApr = (apy: number) => apyToAprPercent(apy * 100)

export const nestFetcher: YieldFetcher = {
  label: 'NEST',
  fetch: async () => {
    const [unified, ...rwa] = await Promise.all([
      fetch(`${NEST_API_URL}/vaults/apy`).then(
        (r) => r.json() as Promise<NestVaultsApyResponse>,
      ),
      ...NEST_RWA_SLUGS.map(({ slug }) =>
        fetch(`${NEST_API_URL}/assets/${slug}/apy`).then(
          (r) => r.json() as Promise<NestSlugApyResponse>,
        ),
      ),
    ])

    const result: Record<string, number> = {}

    for (const v of unified?.data ?? []) {
      const group = NEST_VAULT_GROUPS[v.vaultAddress.toLowerCase()]
      if (!group) continue
      const apy = pickApy(v.navApy)
      if (apy === undefined) continue
      result[group] = toApr(apy)
    }

    for (let i = 0; i < NEST_RWA_SLUGS.length; i++) {
      const apy = pickApy(rwa[i]?.data?.tokens?.[0]?.apy)
      if (apy === undefined) continue
      result[NEST_RWA_SLUGS[i].group] = toApr(apy)
    }

    return result
  },
}
